import React, { useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  Alert,
  Image,
  Modal,
  ScrollView,
  StyleSheet,
  Switch,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import PickerField from '../components/PickerField';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { pickImageFromLibrary, requestNotificationPermission } from '../utils/permissions';
import TonyteCropModal from '../components/TonyteCropModal';
import TonyteModal from '../components/TonyteModal';
import LocationAutocomplete, { type PlaceSuggestion } from '../components/LocationAutocomplete';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import Icon from 'react-native-vector-icons/Feather';
import { addDoc, collection, doc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { Colors, GlobalStyles, Radius, Spacing, Typography } from '../theme';
import type { RootStackParamList } from '../types';
import { findSupportedCity, getDefaultVisibleCity, getVisibleCities, normalizeCityKey } from '../utils/cities';
import TonyteBackground from '../components/TonyteBackground';
import { auth, DEFAULT_COVER, FIRESTORE_COLLECTIONS, firestore, uploadEventCover } from '../firebase';
import { updateActivityInFeedCache } from '../hooks/useFeed';
import { createSeries, updateSeriesFromDate } from '../services/partnerCalendar';
import { isDraftDoc, lifecycleToKeep } from '../utils/partnerCalendar';
import { useProfile } from '../hooks/useProfile';
import { canManageEvent } from '../utils/eventPermissions';
import { INTEREST_CATEGORIES } from '../data/interests';
import { type UploadTagId, UPLOAD_TAGS, VALID_UPLOAD_TAG_IDS, deriveCategoryFromTags } from '../utils/eventTags';
import { geocodeAddress } from '../utils/geocoding';
import { useI18n } from '../i18n';
import { requireVerifiedEmailForAction } from '../utils/verificationGate';
import { useTheme } from '../theme/useTheme';
import { daylightStyle } from '../theme/daylightStyle';
import type { TonyteTheme } from '../theme/themes';
import { validateEventImageAsset } from '../utils/eventImageFraming';

type Props = NativeStackScreenProps<RootStackParamList, 'UploadEvent'>;

const RECURRENCE_TYPES = ['none', 'weekly', 'custom'] as const;

function buildDateTimeISO(date: string, time: string): string | undefined {
  if (!date) return undefined;
  const [y, m, d] = date.split('-').map(Number);
  const [hStr, minStr] = (time || '00:00').split(':');
  return new Date(y, m - 1, d, Number(hStr) || 0, Number(minStr) || 0, 0, 0).toISOString();
}

function getTodayMidnight(): Date {
  const d = new Date(); d.setHours(0, 0, 0, 0); return d;
}
function getTodayStr(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function getNowTimeStr(): string {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}
function parseDateLocal(str: string): Date {
  const [y, m, d] = str.split('-').map(Number);
  return new Date(y, m - 1, d);
}
function maxDate(a: Date, b: Date): Date {
  return a.getTime() > b.getTime() ? a : b;
}
function slugifyProfileId(value: string): string {
  return value.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}
const WEEKDAYS = [
  { label: 'S', value: 0 },
  { label: 'M', value: 1 },
  { label: 'T', value: 2 },
  { label: 'W', value: 3 },
  { label: 'T', value: 4 },
  { label: 'F', value: 5 },
  { label: 'S', value: 6 },
];

const UploadEventScreen: React.FC<Props> = ({ navigation, route }) => {
  const { t } = useI18n();
  const { theme } = useTheme();
  const appearance = useMemo(() => createThemeStyles(theme), [theme]);
  const insets = useSafeAreaInsets();
  const routeEvent = route.params?.event;
  const routeMode = route.params?.mode ?? (routeEvent?.id ? 'edit' : 'create');
  const template = route.params?.template ?? null;
  // From the Partner Calendar: a day to prefill, and for a repeating event
  // whether the edit covers this date only or this and all following dates.
  const initialDate = route.params?.initialDate ?? '';
  const seriesScope = route.params?.seriesScope ?? 'one';

  // For repost: use the event data for prefill but treat it as a new event (no id)
  const isEditing = routeMode === 'edit' && Boolean(routeEvent?.id);
  const isRepost = routeMode === 'repost';
  // editingEvent only set when actually editing (preserves save-as-update path)
  const editingEvent = isEditing ? routeEvent : undefined;
  const isLumaImport = editingEvent?.externalSource?.provider === 'luma';
  // Saving an imported draft publishes it, so label the button accordingly.
  const isUnpublishedImport = isLumaImport && editingEvent?.publishStatus !== 'published';
  const editingSeriesId = (editingEvent as any)?.seriesId as string | undefined;
  // prefillEvent is the source for initial field values (editing or repost)
  const prefillEvent = routeEvent;

  const currentUid = auth.currentUser?.uid || null;
  const { profile } = useProfile(currentUid);
  const visibleCities = useMemo(() => getVisibleCities(profile?.role), [profile?.role]);
  const defaultVisibleCity = useMemo(() => getDefaultVisibleCity(profile?.role), [profile?.role]);

  // Derive initial values: template > prefillEvent > empty
  const initTitle = template ? template.eventTitle : prefillEvent?.title ?? '';
  const initDescription = template ? template.description : prefillEvent?.description ?? '';
  const initVenueName = template ? template.clubName : prefillEvent?.venueName ?? prefillEvent?.locationName ?? '';
  const initHashtags = template
    ? template.tags.join(', ')
    : [...(prefillEvent?.hashtags ?? [])].join(', ');
  const initSelectedTags = (() => {
    const raw = prefillEvent?.tags ?? [];
    return new Set<UploadTagId>(raw.filter(tagId => VALID_UPLOAD_TAG_IDS.has(tagId)) as UploadTagId[]);
  })();
  const initAddress = template ? template.address : prefillEvent?.address ?? prefillEvent?.location ?? '';
  const initCityRaw = template ? template.city : prefillEvent?.city ?? '';
  const initCity = initCityRaw; // kept for template compat
  // Dates: cleared for repost and template (user must pick fresh dates)
  const initStartDate = isEditing ? prefillEvent?.startDate ?? '' : initialDate;
  const initEndDate = isEditing ? prefillEvent?.endDate ?? '' : initialDate;
  const initStartTime = template
    ? template.defaultStartTime
    : isEditing
      ? prefillEvent?.startTime ?? ''
      : '';
  const initEndTime = template
    ? template.defaultEndTime
    : isEditing
      ? prefillEvent?.endTime ?? ''
      : '';
  const initCover = template
    ? template.coverImageUrl || null
    : prefillEvent?.coverPhoto ?? prefillEvent?.coverImageUrl ?? prefillEvent?.imageUrl ?? null;

  const [title, setTitle] = useState(initTitle);
  const [description, setDescription] = useState(initDescription);
  const [venueName, setVenueName] = useState(initVenueName);
  const [lineup, setLineup] = useState(isEditing ? (prefillEvent?.lineup ?? []).join(', ') : '');
  const [hashtags, setHashtags] = useState(initHashtags);
  const [selectedTags, setSelectedTags] = useState<Set<UploadTagId>>(initSelectedTags);
  const [sourceWebsite, setSourceWebsite] = useState(isEditing ? prefillEvent?.sourceLinks?.website ?? '' : '');
  const [sourceInstagram, setSourceInstagram] = useState(isEditing ? prefillEvent?.sourceLinks?.instagram ?? '' : '');
  const [sourceEventPage, setSourceEventPage] = useState(
    isEditing ? prefillEvent?.sourceLinks?.eventPage ?? prefillEvent?.officialLink ?? '' : '',
  );
  const initSelectedPlace: PlaceSuggestion | null = (() => {
    if (!initAddress) return null;
    const ev = prefillEvent as any;
    const lat = ev?.coordinates?.lat ?? ev?.lat ?? 0;
    const lng = ev?.coordinates?.lng ?? ev?.lng ?? 0;
    if (!lat || !lng) return null;
    return {
      placeId: 'prefill',
      displayName: initAddress,
      venueName: initVenueName || initAddress,
      address: initAddress,
      city: initCityRaw,
      lat,
      lng,
    };
  })();
  const [selectedPlace, setSelectedPlace] = useState<PlaceSuggestion | null>(initSelectedPlace);
  const [typedAddress, setTypedAddress] = useState('');
  const [locationWarning, setLocationWarning] = useState('');
  const [locationModalVisible, setLocationModalVisible] = useState(false);
  const [cityPickerOpen, setCityPickerOpen] = useState(false);
  const [eventCityKey, setEventCityKey] = useState(() => {
    const matched = findSupportedCity(normalizeCityKey(initCity));
    return matched?.value ?? defaultVisibleCity.value;
  });
  const [startDate, setStartDate] = useState(initStartDate);
  const [endDate, setEndDate] = useState(initEndDate);
  const [startTime, setStartTime] = useState(initStartTime);
  const [endTime, setEndTime] = useState(initEndTime);
  const [lastEntryTime, setLastEntryTime] = useState(isEditing ? prefillEvent?.lastEntryTime ?? '' : '');
  const [maxParticipants, setMaxParticipants] = useState(String(prefillEvent?.maxParticipants ?? '20'));
  const [saveAsTemplate, setSaveAsTemplate] = useState(false);
  const [priceKnown, setPriceKnown] = useState(!isLumaImport || editingEvent?.isFree != null || editingEvent?.price != null);
  const [isFreeEvent, setIsFreeEvent] = useState(!isLumaImport || editingEvent?.isFree === true);
  const [ticketPrice, setTicketPrice] = useState(isLumaImport && editingEvent?.price != null ? String(editingEvent.price) : '');
  const [recurrencePattern, setRecurrencePattern] = useState<typeof RECURRENCE_TYPES[number]>('none');
  const [recurrenceWeekdays, setRecurrenceWeekdays] = useState<number[]>([]);
  const [recurrenceEndDate, setRecurrenceEndDate] = useState('');
  const [recurrenceOccurrenceCount, setRecurrenceOccurrenceCount] = useState('');
  const [recurrenceExtraDates, setRecurrenceExtraDates] = useState('');
  const [recurrenceSkipDates, setRecurrenceSkipDates] = useState('');
  const [coverImageUri, setCoverImageUri] = useState<string | null>(initCover);
  const [cropUri, setCropUri] = useState('');
  const [showCrop, setShowCrop] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    navigation.setOptions({ gestureEnabled: !showCrop });
    return () => {
      navigation.setOptions({ gestureEnabled: true });
    };
  }, [navigation, showCrop]);

  useEffect(() => {
    if (currentUid && !profile) return;
    if (visibleCities.some(city => city.value === eventCityKey)) return;
    setEventCityKey(defaultVisibleCity.value);
  }, [currentUid, defaultVisibleCity.value, eventCityKey, profile, visibleCities]);

  const handleStartDateChange = (date: string) => {
    setStartDate(date);
    if (endDate && endDate < date) setEndDate('');
  };

  const pickCover = async () => {
    const result = await pickImageFromLibrary({ mediaType: 'photo', quality: 1 });
    const asset = result?.assets?.[0];
    if (asset?.uri) {
      const validationError = validateEventImageAsset(asset);
      if (validationError) {
        Alert.alert(t('uploadEvent.invalidImage'), t(validationError === 'too-large' ? 'uploadEvent.imageTooLarge' : 'uploadEvent.unsupportedImage'));
        return;
      }
      console.log('[Tonyte][ImagePreview] selected image', asset.uri);
      setCropUri(asset.uri);
      setShowCrop(true);
      console.log('[Tonyte][ImagePreview] opening preview', {
        pendingImageType: 'event',
        imagePreviewVisible: true,
      });
      return;
    }
  };

  const handleCropDone = (croppedUri: string) => {
    console.log('[Tonyte][ImagePreview] modal closed by confirm', {
      pendingImageType: 'event',
      uri: croppedUri,
    });
    setCoverImageUri(croppedUri);
    setShowCrop(false);
    setCropUri('');
  };

  const cancelImagePreview = () => {
    console.log('[Tonyte][ImagePreview] modal closed by cancel', {
      pendingImageType: 'event',
      pendingImageUri: cropUri || null,
    });
    setShowCrop(false);
    setCropUri('');
  };

  const toggleWeekday = (weekday: number) => {
    setRecurrenceWeekdays(current =>
      current.includes(weekday) ? current.filter(value => value !== weekday) : [...current, weekday],
    );
  };

  const splitList = (value: string) =>
    value.split(',').map(item => item.trim()).filter(Boolean);

  const recurrenceUntilMinimumDate = startDate
    ? maxDate(getTodayMidnight(), parseDateLocal(startDate))
    : getTodayMidnight();

  useEffect(() => {
    if (!recurrenceEndDate) return;
    const minimumDateKey = startDate && startDate > getTodayStr() ? startDate : getTodayStr();
    if (recurrenceEndDate < minimumDateKey) {
      setRecurrenceEndDate('');
    }
  }, [recurrenceEndDate, startDate]);

  const save = async () => {
    const user = auth.currentUser;
    if (!user) {
      Alert.alert(t('auth.signInRequired'), t('uploadEvent.signInBody'));
      return;
    }
    if (!requireVerifiedEmailForAction()) return;
    if (!title.trim() || !description.trim()) {
      Alert.alert(t('uploadEvent.missingDetails'), t('uploadEvent.missingDetailsBody'));
      return;
    }
    if (!selectedPlace && !typedAddress.trim()) {
      setLocationModalVisible(true);
      return;
    }
    if (!startDate.trim() || !startTime.trim()) {
      Alert.alert(t('uploadEvent.missingDate'), t('uploadEvent.missingDateBody'));
      return;
    }
    const participantLimit = Number(maxParticipants);
    if (!maxParticipants.trim() || !Number.isFinite(participantLimit) || participantLimit < 0) {
      Alert.alert(t('uploadEvent.participantLimit'), t('uploadEvent.participantLimitBody'));
      return;
    }

    const todayS = getTodayStr();
    if (startDate < todayS) {
      Alert.alert(t('uploadEvent.invalidDate'), t('uploadEvent.futureDate'));
      return;
    }
    if (startDate === todayS && startTime < getNowTimeStr()) {
      Alert.alert(t('uploadEvent.invalidTime'), t('uploadEvent.startPast'));
      return;
    }
    const effectiveEndDate = endDate || startDate;
    if (effectiveEndDate < startDate) {
      Alert.alert(t('uploadEvent.invalidDate'), t('uploadEvent.endBeforeStart'));
      return;
    }
    if (endTime && effectiveEndDate === startDate && endTime <= startTime) {
      Alert.alert(t('uploadEvent.invalidTime'), t('uploadEvent.endTimeBeforeStart'));
      return;
    }
    if (recurrencePattern === 'weekly' && recurrenceEndDate.trim() && recurrenceEndDate.trim() < startDate.trim()) {
      Alert.alert(t('uploadEvent.invalidDate'), t('uploadEvent.untilBeforeStart'));
      return;
    }
    if (selectedTags.size === 0) {
      Alert.alert(
        t('uploadEvent.tagRequired'),
        t('uploadEvent.tagRequiredBody'),
      );
      return;
    }
    if (isLumaImport && !coverImageUri) {
      Alert.alert('Missing cover image', 'Add a cover image before publishing this imported event.');
      return;
    }
    if (isLumaImport && !priceKnown) {
      Alert.alert('Price required', 'Confirm whether this event is free or enter its ticket price.');
      return;
    }
    const normalizedTicketPrice = ticketPrice.trim().toLowerCase();
    const hasValidPaidPrice = normalizedTicketPrice === 'paid'
      || (Number.isFinite(Number(normalizedTicketPrice)) && Number(normalizedTicketPrice) >= 0);
    if (isLumaImport && !isFreeEvent && !hasValidPaidPrice) {
      Alert.alert('Invalid price', 'Enter a valid ticket price or use “paid” when the exact fee is explained in the description.');
      return;
    }

    setSaving(true);
    try {
      const hashtagList = splitList(hashtags).map(tag => tag.startsWith('#') ? tag.toLowerCase() : `#${tag.toLowerCase()}`);
      if (isEditing && !canManageEvent(profile ? { ...profile, uid: user.uid } : { uid: user.uid }, editingEvent)) {
        Alert.alert(t('uploadEvent.permissionRequired'), t('uploadEvent.editPermission'));
        return;
      }

      const nowIso = new Date().toISOString();
      const existingCoverPhoto = editingEvent?.coverPhoto || editingEvent?.coverImageUrl || editingEvent?.imageUrl || DEFAULT_COVER;
      // A newly picked cover is a local file — upload it first. Previously the
      // local path itself was saved, so the new image never reached anyone else.
      let selectedCoverPhoto = coverImageUri || existingCoverPhoto;
      let selectedCoverPath: string | undefined;
      if (coverImageUri && !/^https?:\/\//i.test(coverImageUri)) {
        const ownerUid = (editingEvent as any)?.ownerUid || editingEvent?.hostId || user.uid;
        const uploaded = await uploadEventCover(ownerUid, coverImageUri);
        selectedCoverPhoto = uploaded.downloadUrl;
        selectedCoverPath = uploaded.storagePath;
      }

      let resolvedAddress = selectedPlace?.address ?? typedAddress.trim();
      let resolvedVenueName = selectedPlace?.venueName ?? '';
      let resolvedLat: number | null = selectedPlace?.lat ?? null;
      let resolvedLng: number | null = selectedPlace?.lng ?? null;
      let resolvedPlaceId = selectedPlace?.placeId ?? '';
      let locationSource = selectedPlace ? 'autocomplete_selected' : 'manual_unverified';
      setLocationWarning('');

      if (!selectedPlace && typedAddress.trim()) {
        try {
          const cityLabel = findSupportedCity(eventCityKey)?.label ?? '';
          const q = [venueName.trim(), typedAddress.trim(), cityLabel].filter(Boolean).join(', ');
          const geocoded = await geocodeAddress(q);
          if (geocoded) {
            resolvedLat = geocoded.lat;
            resolvedLng = geocoded.lng;
            resolvedAddress = resolvedAddress || geocoded.address;
            resolvedPlaceId = geocoded.placeId || '';
            locationSource = `typed_geocoded_${geocoded.source}`;
          } else {
            setLocationWarning(t('uploadEvent.addressWarning'));
          }
        } catch {
          setLocationWarning(t('uploadEvent.addressWarning'));
        }
      }

      const resolvedCoords = resolvedLat !== null && resolvedLng !== null
        ? { lat: resolvedLat, lng: resolvedLng }
        : null;
      const resolvedOrganizerId = (editingEvent as any)?.organizerId || user.uid;
      // Only fall back to the signed-in user's own name/photo when it's their
      // event. An admin editing a partner's event used to stamp the admin's
      // photo (the Tonyte logo) onto it as the organizer logo.
      const editingOwnEvent = !editingEvent || resolvedOrganizerId === user.uid;
      const resolvedOrganizerName =
        (editingEvent as any)?.organizerName ||
        (editingOwnEvent ? profile?.name || user.displayName : (editingEvent as any)?.hostName) ||
        'Tonyte Organizer';
      const resolvedOrganizerLogo =
        (editingEvent as any)?.organizerLogoUrl ||
        (editingOwnEvent ? profile?.profilePhotoURL || profile?.profileImage || profile?.photo || user.photoURL : '') ||
        '';
      const resolvedVenueDisplayName = venueName.trim() || resolvedVenueName || resolvedAddress;
      const resolvedVenueId = (editingEvent as any)?.venueId || slugifyProfileId(resolvedVenueDisplayName);
      const collaboratorProfileKeys = [
        `organizer_${resolvedOrganizerId}`,
        resolvedVenueId ? `venue_${resolvedVenueId}` : '',
      ].filter(Boolean);

      console.log('[Tonyte][UploadEvent] savedCoordinates', {
        coordinates: resolvedCoords,
        address: resolvedAddress,
        venueName: resolvedVenueName,
        locationSource,
        city: (findSupportedCity(eventCityKey) ?? defaultVisibleCity).label,
      });

      const payload = {
        title: title.trim(),
        description: description.trim(),
        location: resolvedAddress,
        locationName: venueName.trim() || resolvedVenueName || resolvedAddress,
        address: resolvedAddress,
        city: (findSupportedCity(eventCityKey) ?? defaultVisibleCity).label,
        cityKey: eventCityKey,
        placeId: resolvedPlaceId,
        coordinates: resolvedCoords,
        lat: resolvedCoords?.lat ?? null,
        lng: resolvedCoords?.lng ?? null,
        locationSource,
        startsAt: buildDateTimeISO(startDate.trim(), startTime.trim()),
        endsAt: buildDateTimeISO(endDate.trim() || startDate.trim(), endTime.trim() || startTime.trim()),
        startDate: startDate.trim(),
        endDate: endDate.trim() || startDate.trim(),
        startTime: startTime.trim(),
        endTime: endTime.trim() || startTime.trim(),
        lastEntryTime: lastEntryTime.trim(),
        category: deriveCategoryFromTags([...selectedTags]),
        maxParticipants: participantLimit,
        unlimitedParticipants: participantLimit === 0,
        participants: editingEvent?.participants || 0,
        hostId: editingEvent?.hostId || user.uid,
        hostName: editingEvent?.hostName || resolvedOrganizerName,
        primaryHostType: 'organizer',
        primaryHostId: resolvedOrganizerId,
        primaryHostName: resolvedOrganizerName,
        primaryHostAvatarUrl: resolvedOrganizerLogo,
        organizerId: resolvedOrganizerId,
        organizerName: resolvedOrganizerName,
        organizerLogoUrl: resolvedOrganizerLogo,
        organizerUsername: (profile?.username || resolvedOrganizerName).trim().toLowerCase().replace(/\s+/g, '.'),
        venueId: resolvedVenueId,
        venueName: resolvedVenueDisplayName,
        venueLogoUrl: (editingEvent as any)?.venueLogoUrl || '',
        venueUsername: (editingEvent as any)?.venueUsername || slugifyProfileId(resolvedVenueDisplayName).replace(/-/g, '.'),
        collaborators: resolvedVenueId ? [{
          type: 'venue',
          id: resolvedVenueId,
          name: resolvedVenueDisplayName,
          username: slugifyProfileId(resolvedVenueDisplayName).replace(/-/g, '.'),
          avatarUrl: (editingEvent as any)?.venueLogoUrl || '',
          approved: true,
        }] : [],
        collaboratorProfileKeys,
        postedByType: 'organizer',
        lineup: splitList(lineup),
        tags: [...selectedTags],
        hashtags: hashtagList,
        sourceLinks: {
          website: sourceWebsite.trim(),
          instagram: sourceInstagram.trim(),
          eventPage: sourceEventPage.trim(),
        },
        officialLink: sourceEventPage.trim() || sourceWebsite.trim() || sourceInstagram.trim(),
        isFree: isLumaImport ? isFreeEvent : true,
        requiresTicket: isLumaImport ? !isFreeEvent : false,
        ticketPrice: isLumaImport && !isFreeEvent ? ticketPrice.trim() : '',
        price: isLumaImport && !isFreeEvent ? ticketPrice.trim() : '0',
        coverPhoto: selectedCoverPhoto,
        coverImageUrl: selectedCoverPhoto,
        // imageUrl is read first by the feed/detail image resolver (activityImages.ts),
        // so a changed cover must replace it too or the old image keeps showing.
        ...(selectedCoverPath ? { coverPhotoPath: selectedCoverPath, imageUrl: selectedCoverPhoto } : {}),
        entryType: 'official_event',
        type: 'event',
        sourceType: editingEvent?.sourceType || 'user_activity',
        createdBy: editingEvent?.createdBy || user.uid,
        createdByRole: (editingEvent as any)?.createdByRole || profile?.role || 'organizer',
        user: {
          id: (editingEvent as any)?.user?.id || user.uid,
          name: (editingEvent as any)?.user?.name || user.displayName || 'Tonyte Organizer',
          photo: (editingEvent as any)?.user?.photo || user.photoURL || '',
        },
        approved: true,
        isPublic: true,
        status: 'approved',
        publishStatus: 'published',
        incomplete: false,
        missingFields: [],
        needsRevalidation: false,
        visibility: 'public',
        recurrencePattern,
        recurrenceWeekdays,
        recurrenceEndDate: recurrenceEndDate.trim(),
        recurrenceOccurrenceCount: recurrenceOccurrenceCount.trim(),
        recurrenceExtraDates: splitList(recurrenceExtraDates),
        recurrenceSkipDates: splitList(recurrenceSkipDates),
        saveAsTemplate,
        interestedCount: editingEvent?.interestedCount || 0,
        createdAt: isEditing ? editingEvent?.createdAt || serverTimestamp() : serverTimestamp(),
        updatedAt: serverTimestamp(),
      };
      // Editing never silently publishes a draft or revives a cancelled date —
      // that's an explicit Partner Calendar action (Luma drafts keep edit = publish).
      Object.assign(payload, lifecycleToKeep(editingEvent as any) || {});
      if (editingEvent?.id && editingSeriesId && seriesScope === 'future') {
        await updateSeriesFromDate(editingEvent.id, payload);
      } else if (!editingEvent?.id && recurrencePattern !== 'none') {
        // A repeating event is a series: one real event per date (same model as the portal).
        await createSeries(payload, {
          pattern: recurrencePattern,
          weekdays: recurrenceWeekdays,
          until: recurrenceEndDate.trim(),
          count: recurrenceOccurrenceCount.trim(),
          extraDates: splitList(recurrenceExtraDates),
          skipDates: splitList(recurrenceSkipDates),
        });
      } else if (editingEvent?.id) {
        if (editingSeriesId) Object.assign(payload, { seriesException: true });
        const docPath = `${FIRESTORE_COLLECTIONS.activities}/${editingEvent.id}`;
        const ownerFields = {
          createdBy: (editingEvent as any).createdBy,
          hostId: (editingEvent as any).hostId,
          organizerId: (editingEvent as any).organizerId,
          uploadedBy: (editingEvent as any).uploadedBy,
          creatorId: (editingEvent as any).creatorId,
        };
        const ownerIds = Object.values(ownerFields).filter(Boolean);
        const isOwner = ownerIds.includes(user.uid);
        const isAdminRole = profile?.role === 'admin' || profile?.role === 'co-admin';
        console.log('[Tonyte][EventEdit] permission check', {
          currentUserUid: user.uid,
          eventOwnerFields: ownerFields,
          isOwner,
          profileRole: profile?.role,
          isAdminOrCoAdmin: isAdminRole,
          willSucceed: isAdminRole || isOwner,
          docPath,
        });
        await updateDoc(doc(firestore, FIRESTORE_COLLECTIONS.activities, editingEvent.id), payload);
        console.log('[Tonyte][RN][EventEdit] Firestore update success', {
          eventId: editingEvent.id,
          docPath,
        });
        updateActivityInFeedCache({
          ...editingEvent,
          ...payload,
          id: editingEvent.id,
          createdAt: editingEvent.createdAt,
          updatedAt: nowIso,
        } as any);
      } else {
        const docRef = await addDoc(collection(firestore, FIRESTORE_COLLECTIONS.activities), payload);
        console.log('[Tonyte][MapEvents] New event written to Firestore', { eventId: docRef.id, coordinates: payload.coordinates });
        updateActivityInFeedCache({
          ...payload,
          id: docRef.id,
          createdAt: nowIso,
          updatedAt: nowIso,
        } as any);
      }
      requestNotificationPermission().catch(error => {
        console.warn('[Tonyte][RN][Permissions] notification request failed', error);
      });
      navigation.goBack();
    } catch (error) {
      if (editingEvent?.id) {
        console.warn('[Tonyte][RN][EventEdit] Firestore update failed', {
          eventId: editingEvent.id,
          docPath: `${FIRESTORE_COLLECTIONS.activities}/${editingEvent.id}`,
          error,
        });
      }
      Alert.alert(t('uploadEvent.publishFailed'), error instanceof Error ? error.message : t('common.tryAgain'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <TonyteBackground style={[styles.container, appearance.container]} appearanceAware>
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={[styles.content, { paddingTop: insets.top + Spacing.sm, paddingBottom: insets.bottom + Spacing.xl }]}
        keyboardShouldPersistTaps="handled"
      >
        <View style={styles.header}>
          <TouchableOpacity onPress={() => navigation.goBack()}>
            <Text style={[styles.backText, appearance.accentText]}>{t('uploadEvent.back')}</Text>
          </TouchableOpacity>
          <View>
            <Text style={[styles.kicker, appearance.kicker]}>{t('uploadEvent.officialEvent')}</Text>
            <Text style={[styles.headerTitle, appearance.textPrimary]}>
              {isEditing ? t('uploadEvent.editEvent') : isRepost ? t('uploadEvent.repostEvent') : template ? t('uploadEvent.fromTemplate') : t('uploadEvent.createEvent')}
            </Text>
          </View>
          <View style={styles.headerSpacer} />
        </View>

        <TouchableOpacity style={[styles.coverPicker, appearance.coverPicker]} onPress={pickCover}>
          {coverImageUri ? (
            <Image source={{ uri: coverImageUri }} style={styles.coverPreview} resizeMode="cover" />
          ) : (
            <View style={[styles.coverPlaceholder, appearance.coverPlaceholder]}>
              <Icon name="camera" size={36} color={theme.colors.iconFaint} />
              <Text style={[styles.coverPlaceholderText, appearance.textMuted]}>{t('uploadEvent.addCover')}</Text>
            </View>
          )}
        </TouchableOpacity>

        <Field value={title} onChangeText={setTitle} placeholder={t('uploadEvent.title')} />
        <Field value={description} onChangeText={setDescription} placeholder={t('uploadEvent.description')} multiline />
        <Field value={venueName} onChangeText={setVenueName} placeholder={t('uploadEvent.venueOrganizer')} />
        <Field value={lineup} onChangeText={setLineup} placeholder={t('uploadEvent.lineup')} />
        <HashtagField value={hashtags} onChange={setHashtags} />
        <Field value={sourceWebsite} onChangeText={setSourceWebsite} placeholder={t('uploadEvent.websiteUrl')} keyboardType="url" />
        <Field value={sourceInstagram} onChangeText={setSourceInstagram} placeholder={t('uploadEvent.instagramUrl')} keyboardType="url" />
        <Field value={sourceEventPage} onChangeText={setSourceEventPage} placeholder={t('uploadEvent.sourceUrl')} keyboardType="url" />

        <Section title={t('uploadEvent.location')} detail={t('uploadEvent.locationHelp')}>
          <LocationAutocomplete
            value={selectedPlace}
            cityKey={eventCityKey}
            onChange={(place) => { setSelectedPlace(place); if (place) setLocationWarning(''); }}
            onTextChange={setTypedAddress}
          />
          {locationWarning ? (
            <Text style={[styles.locationWarning, appearance.accentSecondaryText]}>{locationWarning}</Text>
          ) : null}
        </Section>

        <Section title={t('uploadEvent.city')} detail={t('uploadEvent.cityHelp')}>
          <TouchableOpacity
            activeOpacity={0.82}
            style={[uploadStyles.cityPickerBtn, appearance.cityPickerBtn]}
            onPress={() => setCityPickerOpen(true)}
          >
            <Icon name="map-pin" size={15} color={theme.colors.iconSubtle} />
            <Text style={[uploadStyles.cityPickerBtnText, appearance.textPrimary]}>
              {visibleCities.find(c => c.value === eventCityKey)?.label ?? defaultVisibleCity.label}
            </Text>
            <Icon name="chevron-down" size={15} color={theme.colors.iconSubtle} />
          </TouchableOpacity>
        </Section>

        <Section title={t('uploadEvent.dateRange')} detail={t('uploadEvent.dateRangeHelp')}>
          <View style={styles.rowInputs}>
            <PickerField
              value={startDate}
              onChange={handleStartDateChange}
              placeholder={t('uploadEvent.dateFrom')}
              mode="date"
              minimumDate={getTodayMidnight()}
              noMargin
            />
            <PickerField
              value={endDate}
              onChange={setEndDate}
              placeholder={t('uploadEvent.dateTo')}
              mode="date"
              minimumDate={startDate ? (() => {
                const sd = parseDateLocal(startDate);
                const today = getTodayMidnight();
                return sd > today ? sd : today;
              })() : getTodayMidnight()}
              noMargin
            />
          </View>
          <View style={styles.rowInputs}>
            <PickerField
              value={startTime}
              onChange={setStartTime}
              placeholder={t('uploadEvent.timeFrom')}
              mode="time"
              minimumDate={startDate === getTodayStr() ? new Date() : undefined}
              onReject={() => Alert.alert(t('uploadEvent.invalidTime'), t('uploadEvent.startPast'))}
              noMargin
            />
            <PickerField
              value={endTime}
              onChange={setEndTime}
              placeholder={t('uploadEvent.timeTo')}
              mode="time"
              minimumDate={(() => {
                const sameDay = (endDate || startDate) === startDate && Boolean(startDate);
                if (!sameDay) return undefined;
                const todayS = getTodayStr();
                if (startTime) {
                  const [h, m] = startTime.split(':').map(Number);
                  const d = new Date(); d.setHours(h, m, 0, 0);
                  return startDate === todayS && new Date() > d ? new Date() : d;
                }
                return startDate === todayS ? new Date() : undefined;
              })()}
              onReject={() => Alert.alert(t('uploadEvent.invalidTime'), t('uploadEvent.endTimeBeforeStart'))}
              noMargin
            />
          </View>
          <Field
            value={lastEntryTime}
            onChangeText={setLastEntryTime}
            placeholder={t('uploadEvent.lastEntry')}
            helper={t('uploadEvent.lastEntryHelp')}
            noMargin
          />
        </Section>

        <View style={[tagSectionStyles.section, appearance.section]}>
          <View style={tagSectionStyles.header}>
            <View>
              <Text style={[tagSectionStyles.title, appearance.textMuted]}>{t('uploadEvent.eventTags')}</Text>
              <Text style={[tagSectionStyles.subtitle, appearance.textSecondary]}>
                {t('uploadEvent.tagsHelp')}
              </Text>
            </View>
            {selectedTags.size > 0 ? (
              <View style={[tagSectionStyles.countBadge, appearance.accentBackground]}>
                <Text style={tagSectionStyles.countText}>{selectedTags.size}</Text>
              </View>
            ) : null}
          </View>
          <View style={styles.catGrid}>
            {UPLOAD_TAGS.map(tag => {
              const active = selectedTags.has(tag.id);
              return (
                <TouchableOpacity
                  key={tag.id}
                  style={[
                    tagSectionStyles.chip,
                    appearance.chip,
                    active && tagSectionStyles.chipActive,
                    active && appearance.chipActive,
                  ]}
                  onPress={() => setSelectedTags(current => {
                    const next = new Set(current);
                    if (next.has(tag.id)) { next.delete(tag.id); } else { next.add(tag.id); }
                    return next;
                  })}
                  activeOpacity={0.78}
                >
                  <Icon
                    name={tag.icon}
                    size={12}
                    color={active ? theme.colors.accent : theme.colors.iconSubtle}
                  />
                  <Text style={[
                    tagSectionStyles.chipText,
                    appearance.chipText,
                    active && tagSectionStyles.chipTextActive,
                    active && appearance.accentText,
                  ]}>
                    {t(`feedFilters.${tag.id}`, tag.label)}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </View>
        </View>

        <Section title={t('uploadEvent.maxParticipants')} detail={t('uploadEvent.maxParticipantsHelp')}>
          <Field
            value={maxParticipants}
            onChangeText={setMaxParticipants}
            placeholder={t('uploadEvent.maxParticipantsPlaceholder')}
            keyboardType="numeric"
            noMargin
          />
        </Section>

        {isLumaImport ? (
          <Section title="Ticket price" detail="Confirm the imported pricing. Use “paid” when the exact or variable fee is already explained in the description.">
            <View style={styles.switchRow}>
              <Text style={[styles.switchLabel, appearance.textPrimary]}>Pricing confirmed</Text>
              <Switch value={priceKnown} onValueChange={setPriceKnown} trackColor={{ true: theme.colors.accent, false: theme.colors.borderStrong }} thumbColor="#fff" />
            </View>
            {priceKnown ? (
              <>
                <View style={styles.switchRow}>
                  <Text style={[styles.switchLabel, appearance.textPrimary]}>Free event</Text>
                  <Switch value={isFreeEvent} onValueChange={setIsFreeEvent} trackColor={{ true: theme.colors.accent, false: theme.colors.borderStrong }} thumbColor="#fff" />
                </View>
                {!isFreeEvent ? <Field value={ticketPrice} onChangeText={setTicketPrice} placeholder="Amount in EUR or paid" noMargin /> : null}
              </>
            ) : null}
          </Section>
        ) : <Section title={t('uploadEvent.freeEvent')} detail={t('uploadEvent.freeEventHelp')} />}

        <Section title={t('uploadEvent.saveTemplate')} detail={t('uploadEvent.saveTemplateHelp')}>
          <View style={styles.switchRow}>
            <Text style={[styles.switchLabel, appearance.textPrimary]}>{t('uploadEvent.saveCurrentTemplate')}</Text>
            <Switch
              value={saveAsTemplate}
              onValueChange={setSaveAsTemplate}
              trackColor={{ true: theme.colors.accent, false: theme.colors.borderStrong }}
              thumbColor="#fff"
            />
          </View>
        </Section>

        {editingSeriesId ? (
          <Section
            title={t('uploadEvent.recurring')}
            detail={seriesScope === 'future' ? t('partnerCalendar.editingFuture') : t('partnerCalendar.editingOne')}
          />
        ) : (
        <Section title={t('uploadEvent.recurring')} detail={t('uploadEvent.recurringHelp')}>
          <View style={styles.catGrid}>
            {RECURRENCE_TYPES.map(type => (
              <TouchableOpacity
                key={type}
                style={[
                  styles.catChip,
                  appearance.chip,
                  recurrencePattern === type && styles.catChipActive,
                  recurrencePattern === type && appearance.chipActive,
                ]}
                onPress={() => setRecurrencePattern(type)}
              >
                <Text style={[
                  styles.catText,
                  appearance.chipText,
                  recurrencePattern === type && styles.catTextActive,
                  recurrencePattern === type && appearance.accentText,
                ]}>
                  {type === 'none' ? t('uploadEvent.oneTime') : type === 'weekly' ? t('uploadEvent.weekly') : t('uploadEvent.custom')}
                </Text>
              </TouchableOpacity>
            ))}
          </View>

          {recurrencePattern === 'weekly' ? (
            <>
              <Label text={t('uploadEvent.repeatEvery')} />
              <View style={styles.weekdayGrid}>
                {WEEKDAYS.map(day => (
                  <TouchableOpacity
                    key={`${day.label}-${day.value}`}
                    style={[
                      styles.weekday,
                      appearance.weekday,
                      recurrenceWeekdays.includes(day.value) && styles.weekdayActive,
                      recurrenceWeekdays.includes(day.value) && appearance.weekdayActive,
                    ]}
                    onPress={() => toggleWeekday(day.value)}
                  >
                    <Text style={[
                      styles.weekdayText,
                      appearance.chipText,
                      recurrenceWeekdays.includes(day.value) && styles.weekdayTextActive,
                    ]}>
                      {day.label}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
              <View style={styles.rowInputs}>
                <PickerField
                  value={recurrenceEndDate}
                  onChange={setRecurrenceEndDate}
                  placeholder={t('uploadEvent.until')}
                  mode="date"
                  minimumDate={recurrenceUntilMinimumDate}
                  onReject={() => Alert.alert(t('uploadEvent.invalidDate'), t('uploadEvent.untilBeforeStart'))}
                  noMargin
                />
                <Field value={recurrenceOccurrenceCount} onChangeText={setRecurrenceOccurrenceCount} placeholder={t('uploadEvent.occurrences')} keyboardType="numeric" noMargin />
              </View>
            </>
          ) : null}

          {recurrencePattern === 'custom' ? (
            <Field
              value={recurrenceExtraDates}
              onChangeText={setRecurrenceExtraDates}
              placeholder={t('uploadEvent.customDates')}
              helper={t('uploadEvent.customDatesHelp')}
              noMargin
            />
          ) : null}

          {recurrencePattern !== 'none' ? (
            <Field
              value={recurrenceSkipDates}
              onChangeText={setRecurrenceSkipDates}
              placeholder={t('uploadEvent.skipDates')}
              helper={t('uploadEvent.skipDatesHelp')}
              noMargin
            />
          ) : null}
        </Section>
        )}

        <TouchableOpacity style={[GlobalStyles.btnPrimary, styles.submitBtn]} onPress={save} disabled={saving}>
          {saving ? (
            <ActivityIndicator color="#fff" />
          ) : (
            <Text style={GlobalStyles.btnPrimaryText}>
              {isUnpublishedImport ? t('uploadEvent.publishEvent') : isEditing && editingEvent && isDraftDoc(editingEvent as any) ? t('partnerCalendar.saveDraft') : isEditing ? t('uploadEvent.saveEvent') : isRepost ? t('uploadEvent.publishRepost') : t('uploadEvent.publishEvent')}
            </Text>
          )}
        </TouchableOpacity>
      </ScrollView>

      <Modal
        visible={cityPickerOpen}
        transparent
        animationType="fade"
        onRequestClose={() => setCityPickerOpen(false)}
      >
        <TouchableOpacity
          activeOpacity={1}
          style={[uploadStyles.cityModalBackdrop, appearance.modalBackdrop]}
          onPress={() => setCityPickerOpen(false)}
        >
          <TouchableOpacity activeOpacity={1} style={[uploadStyles.cityModal, appearance.cityModal]}>
            <View style={uploadStyles.cityModalHeader}>
              <Text style={[uploadStyles.cityModalTitle, appearance.textPrimary]}>{t('uploadEvent.selectCity')}</Text>
              <TouchableOpacity onPress={() => setCityPickerOpen(false)}>
                <Icon name="x" size={18} color={theme.colors.icon} />
              </TouchableOpacity>
            </View>
            {visibleCities.map(city => {
              const selected = city.value === eventCityKey;
              return (
                <TouchableOpacity
                  key={city.value}
                  style={[
                    uploadStyles.cityOption,
                    appearance.cityOption,
                    selected && uploadStyles.cityOptionActive,
                    selected && appearance.cityOptionActive,
                  ]}
                  onPress={() => {
                    setEventCityKey(city.value);
                    setCityPickerOpen(false);
                  }}
                >
                  <Text style={[
                    uploadStyles.cityOptionText,
                    appearance.textSecondary,
                    selected && uploadStyles.cityOptionTextActive,
                    selected && appearance.accentText,
                  ]}>
                    {city.label}
                  </Text>
                  {selected ? <Icon name="check" size={16} color={theme.colors.accent} /> : null}
                </TouchableOpacity>
              );
            })}
          </TouchableOpacity>
        </TouchableOpacity>
      </Modal>
      {showCrop && cropUri ? (
        <TonyteCropModal
          session={{ kind: 'event', uri: cropUri, aspectRatio: '8:5' }}
          onCrop={handleCropDone}
          onCancel={cancelImagePreview}
        />
      ) : null}
      <TonyteModal
        visible={locationModalVisible}
        title={t('uploadEvent.locationRequired')}
        message={t('uploadEvent.locationRequiredBody')}
        icon="map-pin"
        iconColor={theme.colors.accent}
        buttons={[{ label: t('uploadEvent.ok'), variant: 'primary', onPress: () => setLocationModalVisible(false) }]}
        onDismiss={() => setLocationModalVisible(false)}
      />
    </TonyteBackground>
  );
};

const Label: React.FC<{ text: string }> = ({ text }) => {
  const { theme } = useTheme();
  return <Text style={[styles.label, daylightStyle(theme, { color: theme.colors.textSecondary })]}>{text}</Text>;
};

const Field: React.FC<React.ComponentProps<typeof TextInput> & { helper?: string; noMargin?: boolean }> = ({
  helper,
  noMargin,
  style,
  multiline,
  ...props
}) => {
  const { theme } = useTheme();
  const fieldTheme = daylightStyle(theme, {
    backgroundColor: theme.colors.inputBackground,
    borderColor: theme.colors.borderStrong,
    color: theme.colors.textPrimary,
    ...theme.shadows.card,
  });
  return (
    <View style={!noMargin && styles.fieldWrap}>
      <TextInput
        style={[GlobalStyles.inputBase, multiline && styles.textarea, fieldTheme, style]}
        placeholderTextColor={theme.colors.textMuted}
        multiline={multiline}
        numberOfLines={multiline ? 4 : 1}
        {...props}
      />
      {helper ? (
        <Text style={[styles.helper, daylightStyle(theme, { color: theme.colors.textMuted })]}>{helper}</Text>
      ) : null}
    </View>
  );
};

const DEFAULT_HASHTAGS = [
  '#travel', '#food', '#nightlife', '#fitness', '#music',
  '#art', '#social', '#tech', '#outdoors', '#lifestyle',
];

const HASHTAG_CATEGORIES: { name: string; tags: string[] }[] = [
  { name: 'Popular', tags: DEFAULT_HASHTAGS },
  ...INTEREST_CATEGORIES.map(cat => ({
    name: cat.name,
    tags: cat.interests.map(i => `#${i.toLowerCase().replace(/[^a-z0-9]+/g, '')}`),
  })),
];

const MAX_HASHTAGS = 20;

const normalizeTag = (raw: string): string => {
  const trimmed = raw.trim();
  if (!trimmed) return '';
  const h = trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
  return h.toLowerCase().replace(/\s+/g, '');
};

const parseChips = (value: string): string[] =>
  value.split(',').map(s => s.trim()).filter(Boolean);

const HashtagField: React.FC<{ value: string; onChange: (v: string) => void }> = ({ value, onChange }) => {
  const { t } = useI18n();
  const { theme } = useTheme();
  const appearance = useMemo(() => createThemeStyles(theme), [theme]);
  const [inputText, setInputText] = useState('');
  const [expanded, setExpanded] = useState(false);
  const chips = parseChips(value);
  const chipSet = new Set(chips.map(c => c.toLowerCase()));

  const commitInput = (raw: string) => {
    const tag = normalizeTag(raw);
    if (!tag || chips.length >= MAX_HASHTAGS) return;
    if (chipSet.has(tag.toLowerCase())) return;
    onChange([...chips, tag].join(', '));
  };

  const handleTextChange = (text: string) => {
    const separator = text.includes(',') ? ',' : text.endsWith(' ') ? ' ' : null;
    if (separator) {
      const parts = text.split(separator);
      commitInput(parts[0]);
      setInputText(parts.slice(1).join(separator).trimStart());
    } else {
      setInputText(text);
    }
  };

  const handleSubmit = () => {
    commitInput(inputText);
    setInputText('');
  };

  const toggleTag = (tag: string) => {
    if (chipSet.has(tag.toLowerCase())) {
      onChange(chips.filter(c => c.toLowerCase() !== tag.toLowerCase()).join(', '));
    } else {
      commitInput(tag);
    }
  };

  const removeChip = (chip: string) => {
    onChange(chips.filter(c => c !== chip).join(', '));
  };

  const clearAll = () => {
    onChange('');
    setInputText('');
  };

  const query = inputText.trim().toLowerCase().replace(/^#/, '');
  const filteredCategories = HASHTAG_CATEGORIES.map(cat => ({
    ...cat,
    tags: query
      ? cat.tags.filter(tag => tag.replace('#', '').includes(query))
      : cat.tags,
  })).filter(cat => cat.tags.length > 0);

  return (
    <View style={[hashStyles.container, appearance.section, { marginTop: Spacing.xs }]}>
      {/* Header */}
      <TouchableOpacity
        style={hashStyles.header}
        onPress={() => setExpanded(current => !current)}
        activeOpacity={0.82}
      >
        <View>
          <Text style={[hashStyles.title, appearance.textMuted]}>{t('uploadEvent.hashtags')}</Text>
          <Text style={[hashStyles.count, appearance.textSecondary]}>{chips.length}/{MAX_HASHTAGS} {t('uploadEvent.selected')}</Text>
        </View>
        <View style={hashStyles.headerActions}>
          {chips.length > 0 && (
            <TouchableOpacity
              onPress={event => {
                event.stopPropagation();
                clearAll();
              }}
              style={[hashStyles.clearBtn, appearance.chip]}
              activeOpacity={0.72}
            >
              <Text style={[hashStyles.clearText, appearance.textSecondary]}>{t('common.clear')}</Text>
            </TouchableOpacity>
          )}
          <Icon name={expanded ? 'chevron-up' : 'chevron-down'} size={18} color={theme.colors.iconSubtle} />
        </View>
      </TouchableOpacity>

      {/* Selected chips */}
      {chips.length > 0 && (
        <View style={hashStyles.chipsWrap}>
          {chips.map(chip => (
            <TouchableOpacity
              key={chip}
              onPress={() => removeChip(chip)}
              style={[hashStyles.chip, appearance.secondaryChipActive]}
              activeOpacity={0.75}
            >
              <Text style={[hashStyles.chipText, appearance.accentSecondaryText]}>{chip}</Text>
              <Icon name="x" size={11} color={theme.colors.accentSecondary} />
            </TouchableOpacity>
          ))}
        </View>
      )}

      {/* Search input */}
      <View style={[hashStyles.inputRow, appearance.input]}>
        <Icon name="hash" size={15} color={theme.colors.iconFaint} />
        <TextInput
          value={inputText}
          onChangeText={handleTextChange}
          onSubmitEditing={handleSubmit}
          onFocus={() => setExpanded(true)}
          placeholder={t('uploadEvent.addHashtags')}
          placeholderTextColor={theme.colors.textMuted}
          returnKeyType="done"
          blurOnSubmit={false}
          style={[hashStyles.input, appearance.textPrimary]}
        />
        <TouchableOpacity
          style={[hashStyles.addBtn, appearance.accentBackground]}
          onPress={() => {
            if (inputText.trim()) {
              handleSubmit();
            } else {
              setExpanded(current => !current);
            }
          }}
          activeOpacity={0.78}
        >
          <Icon name={inputText.trim() ? 'plus' : expanded ? 'chevron-up' : 'chevron-down'} size={14} color="#fff" />
        </TouchableOpacity>
      </View>

      {/* Predefined hashtag list */}
      {expanded ? (
        <>
          {filteredCategories.map(cat => (
            <View key={cat.name} style={hashStyles.categoryBlock}>
              <Text style={[hashStyles.categoryName, appearance.textMuted]}>{cat.name}</Text>
              <View style={hashStyles.chipsWrap}>
                {cat.tags.map(tag => {
                  const selected = chipSet.has(tag.toLowerCase());
                  return (
                    <TouchableOpacity
                      key={tag}
                      onPress={() => toggleTag(tag)}
                      style={[
                        hashStyles.presetChip,
                        appearance.chip,
                        selected && hashStyles.presetChipSelected,
                        selected && appearance.secondaryChipActive,
                      ]}
                      activeOpacity={0.72}
                      disabled={!selected && chips.length >= MAX_HASHTAGS}
                    >
                      <Text style={[
                        hashStyles.presetChipText,
                        appearance.chipText,
                        selected && hashStyles.presetChipTextSelected,
                        selected && appearance.accentSecondaryText,
                      ]}>
                        {tag}
                      </Text>
                    </TouchableOpacity>
                  );
                })}
              </View>
            </View>
          ))}

          <View style={hashStyles.collapseRow}>
            <TouchableOpacity style={[hashStyles.collapseBtn, appearance.chip]} onPress={() => setExpanded(false)} activeOpacity={0.76}>
              <Icon name="chevron-up" size={13} color={theme.colors.iconSubtle} />
              <Text style={[hashStyles.collapseText, appearance.textSecondary]}>{t('uploadEvent.collapseHashtags')}</Text>
            </TouchableOpacity>
          </View>
        </>
      ) : null}

      {/* Helper */}
      <Text style={[hashStyles.helper, appearance.textMuted]}>
        {t('uploadEvent.hashtagsHelp')}
      </Text>
    </View>
  );
};

const Section: React.FC<{ title: string; detail?: string; children?: React.ReactNode }> = ({ title, detail, children }) => {
  const { theme } = useTheme();
  const appearance = useMemo(() => createThemeStyles(theme), [theme]);
  return (
    <View style={[styles.section, appearance.section]}>
      <Text style={[styles.sectionTitle, appearance.textPrimary]}>{title}</Text>
      {detail ? <Text style={[styles.sectionDetail, appearance.textMuted]}>{detail}</Text> : null}
      {children ? <View style={styles.sectionBody}>{children}</View> : null}
    </View>
  );
};

const uploadStyles = StyleSheet.create({
  cityPickerBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    height: 48,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.12)',
    backgroundColor: 'rgba(255,255,255,0.06)',
    paddingHorizontal: 14,
  },
  cityPickerBtnText: {
    flex: 1,
    color: 'rgba(255,255,255,0.82)',
    fontSize: 15,
  },
  cityModalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.6)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  cityModal: {
    width: '100%',
    maxWidth: 400,
    borderRadius: 16,
    backgroundColor: '#0b111d',
    padding: 14,
  },
  cityModalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 10,
  },
  cityModalTitle: {
    color: Colors.textPrimary,
    fontSize: 16,
    fontWeight: Typography.extrabold,
  },
  cityOption: {
    minHeight: 48,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.08)',
    backgroundColor: 'rgba(255,255,255,0.04)',
    paddingHorizontal: 13,
    paddingVertical: 10,
    marginBottom: 8,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  cityOptionActive: {
    borderColor: 'rgba(242,125,38,0.62)',
    backgroundColor: 'rgba(242,125,38,0.14)',
  },
  cityOptionText: {
    color: 'rgba(255,255,255,0.78)',
    fontSize: 15,
  },
  cityOptionTextActive: {
    color: Colors.sunset,
    fontWeight: Typography.extrabold,
  },
});

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: Colors.bg },
  scroll: { flex: 1, backgroundColor: 'transparent' },
  content: { paddingHorizontal: Spacing.base, gap: Spacing.sm },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: Spacing.md,
  },
  backText: { color: Colors.sunset, fontSize: Typography.base },
  kicker: {
    alignSelf: 'center',
    borderRadius: Radius.pill,
    backgroundColor: 'rgba(242,125,38,0.15)',
    color: Colors.sunset,
    fontSize: 10,
    fontWeight: Typography.bold,
    letterSpacing: 2,
    paddingHorizontal: 10,
    paddingVertical: 4,
    textTransform: 'uppercase',
  },
  headerTitle: {
    color: Colors.textPrimary,
    fontSize: Typography.xl,
    fontWeight: Typography.bold,
    marginTop: Spacing.xs,
    textAlign: 'center',
  },
  headerSpacer: { width: 60 },
  coverPicker: {
    height: 180,
    borderRadius: Radius.xl,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: Colors.border,
    marginBottom: Spacing.sm,
  },
  coverPreview: { width: '100%', height: '100%' },
  coverPlaceholder: {
    flex: 1,
    backgroundColor: Colors.bgCard,
    alignItems: 'center',
    justifyContent: 'center',
    gap: Spacing.sm,
  },
  coverPlaceholderText: { color: Colors.textMuted, fontSize: Typography.sm },
  fieldWrap: { marginTop: Spacing.xs },
  label: {
    color: Colors.textSecondary,
    fontSize: Typography.sm,
    fontWeight: Typography.semibold,
    marginTop: Spacing.xs,
    marginBottom: Spacing.xs,
  },
  helper: {
    color: Colors.textMuted,
    fontSize: Typography.xs,
    lineHeight: 16,
    marginTop: Spacing.xs,
  },
  textarea: { minHeight: 110, textAlignVertical: 'top' },
  section: {
    borderRadius: Radius.xl,
    borderWidth: 1,
    borderColor: Colors.border,
    backgroundColor: 'rgba(255,255,255,0.05)',
    padding: Spacing.base,
    marginTop: Spacing.sm,
    gap: Spacing.sm,
  },
  sectionTitle: {
    color: Colors.textPrimary,
    fontSize: Typography.sm,
    fontWeight: Typography.bold,
  },
  sectionDetail: {
    color: Colors.textMuted,
    fontSize: Typography.xs,
    lineHeight: 16,
  },
  sectionBody: { gap: Spacing.sm },
  rowInputs: { flexDirection: 'row', gap: Spacing.md, marginTop: Spacing.sm },
  rowInput: { flex: 1 },
  catGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: Spacing.sm },
  catChip: {
    paddingHorizontal: Spacing.md,
    paddingVertical: Spacing.xs,
    borderRadius: Radius.pill,
    borderWidth: 1,
    borderColor: Colors.border,
    backgroundColor: Colors.bgInput,
  },
  catChipActive: { borderColor: Colors.sunset, backgroundColor: 'rgba(242,125,38,0.2)' },
  catText: { color: Colors.textSecondary, fontSize: Typography.xs, fontWeight: Typography.semibold },
  catTextActive: { color: Colors.sunset },
  switchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: Spacing.md,
  },
  switchLabel: { color: Colors.textPrimary, fontSize: Typography.sm, fontWeight: Typography.semibold, flex: 1 },
  weekdayGrid: { flexDirection: 'row', gap: 6 },
  weekday: {
    flex: 1,
    minHeight: 38,
    borderRadius: Radius.md,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.08)',
    borderWidth: 1,
    borderColor: Colors.border,
  },
  weekdayActive: { backgroundColor: Colors.sunset, borderColor: Colors.sunset },
  weekdayText: { color: Colors.textSecondary, fontSize: Typography.xs, fontWeight: Typography.bold },
  weekdayTextActive: { color: '#fff' },
  submitBtn: { marginTop: Spacing.xl },
  locationWarning: {
    color: Colors.turquoise,
    fontSize: Typography.xs,
    lineHeight: 16,
    opacity: 0.8,
  },
});

const tagSectionStyles = StyleSheet.create({
  section: {
    borderRadius: Radius.xl,
    borderWidth: 1,
    borderColor: Colors.border,
    backgroundColor: 'rgba(255,255,255,0.05)',
    padding: Spacing.base,
    marginTop: Spacing.sm,
    gap: Spacing.sm,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  title: {
    color: Colors.textMuted,
    fontSize: 10,
    fontWeight: Typography.bold,
    letterSpacing: 1.5,
    textTransform: 'uppercase',
  },
  subtitle: {
    color: 'rgba(255,255,255,0.30)',
    fontSize: Typography.xs,
    marginTop: 2,
  },
  countBadge: {
    width: 22,
    height: 22,
    borderRadius: 11,
    backgroundColor: Colors.sunset,
    alignItems: 'center',
    justifyContent: 'center',
  },
  countText: {
    color: '#fff',
    fontSize: 11,
    fontWeight: Typography.extrabold,
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    paddingHorizontal: Spacing.md,
    paddingVertical: Spacing.xs,
    borderRadius: Radius.pill,
    borderWidth: 1,
    borderColor: Colors.border,
    backgroundColor: 'rgba(255,255,255,0.05)',
  },
  chipActive: {
    borderColor: 'rgba(242,125,38,0.55)',
    backgroundColor: 'rgba(242,125,38,0.18)',
  },
  chipText: {
    color: 'rgba(255,255,255,0.60)',
    fontSize: Typography.xs,
    fontWeight: Typography.semibold,
  },
  chipTextActive: {
    color: Colors.sunset,
    fontWeight: Typography.bold,
  },
});

const hashStyles = StyleSheet.create({
  container: {
    borderRadius: Radius.xl,
    borderWidth: 1,
    borderColor: Colors.border,
    backgroundColor: 'rgba(255,255,255,0.05)',
    padding: Spacing.base,
    gap: Spacing.sm,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
  },
  title: {
    color: Colors.textMuted,
    fontSize: 10,
    fontWeight: Typography.bold,
    letterSpacing: 1.5,
    textTransform: 'uppercase',
  },
  count: {
    color: 'rgba(255,255,255,0.35)',
    fontSize: Typography.xs,
    marginTop: 2,
  },
  clearBtn: {
    borderRadius: Radius.pill,
    borderWidth: 1,
    borderColor: Colors.border,
    paddingHorizontal: Spacing.md,
    paddingVertical: Spacing.xs,
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.sm,
  },
  clearText: {
    color: 'rgba(255,255,255,0.55)',
    fontSize: Typography.xs,
    fontWeight: Typography.bold,
  },
  chipsWrap: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: Spacing.sm,
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    borderRadius: Radius.pill,
    borderWidth: 1,
    borderColor: 'rgba(38,208,206,0.35)',
    backgroundColor: 'rgba(38,208,206,0.15)',
    paddingHorizontal: Spacing.md,
    paddingVertical: Spacing.xs,
  },
  chipText: {
    color: Colors.turquoise,
    fontSize: Typography.xs,
    fontWeight: Typography.bold,
  },
  inputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.sm,
    borderRadius: Radius.lg,
    borderWidth: 1,
    borderColor: Colors.border,
    backgroundColor: 'rgba(255,255,255,0.07)',
    paddingHorizontal: Spacing.md,
    minHeight: 46,
    paddingVertical: Spacing.sm,
  },
  input: {
    flex: 1,
    color: Colors.textPrimary,
    fontSize: Typography.sm,
    padding: 0,
  },
  addBtn: {
    width: 30,
    height: 30,
    borderRadius: 15,
    backgroundColor: Colors.sunset,
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: Colors.sunset,
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.28,
    shadowRadius: 10,
    elevation: 4,
  },
  helper: {
    color: Colors.textMuted,
    fontSize: Typography.xs,
    lineHeight: 16,
  },
  categoryBlock: {
    gap: Spacing.xs,
  },
  categoryName: {
    color: 'rgba(255,255,255,0.35)',
    fontSize: 10,
    fontWeight: Typography.bold,
    letterSpacing: 1.5,
    textTransform: 'uppercase',
    marginBottom: Spacing.xs,
  },
  presetChip: {
    paddingHorizontal: Spacing.md,
    paddingVertical: Spacing.xs,
    borderRadius: Radius.pill,
    borderWidth: 1,
    borderColor: Colors.border,
    backgroundColor: 'rgba(255,255,255,0.06)',
  },
  presetChipSelected: {
    borderColor: 'rgba(38,208,206,0.40)',
    backgroundColor: 'rgba(38,208,206,0.18)',
  },
  presetChipText: {
    color: 'rgba(255,255,255,0.72)',
    fontSize: Typography.xs,
    fontWeight: Typography.semibold,
  },
  presetChipTextSelected: {
    color: Colors.turquoise,
  },
  collapseRow: {
    alignItems: 'center',
    paddingTop: Spacing.xs,
  },
  collapseBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    borderRadius: Radius.pill,
    borderWidth: 1,
    borderColor: Colors.border,
    backgroundColor: 'rgba(255,255,255,0.04)',
    paddingHorizontal: Spacing.md,
    paddingVertical: Spacing.xs,
  },
  collapseText: {
    color: 'rgba(255,255,255,0.62)',
    fontSize: Typography.xs,
    fontWeight: Typography.bold,
  },
});

const createThemeStyles = (theme: TonyteTheme) => StyleSheet.create({
  container: daylightStyle(theme, { backgroundColor: theme.colors.background }),
  kicker: daylightStyle(theme, {
    backgroundColor: theme.colors.chipBackgroundActive,
    borderColor: 'rgba(242,125,38,0.24)',
    borderWidth: 1,
    color: theme.colors.accent,
  }),
  coverPicker: daylightStyle(theme, {
    backgroundColor: theme.colors.surfaceElevated,
    borderColor: theme.colors.cardBorder,
    ...theme.shadows.card,
  }),
  coverPlaceholder: daylightStyle(theme, { backgroundColor: theme.colors.surfaceSoft }),
  cityPickerBtn: daylightStyle(theme, {
    backgroundColor: theme.colors.inputBackground,
    borderColor: theme.colors.borderStrong,
    ...theme.shadows.card,
  }),
  section: daylightStyle(theme, {
    backgroundColor: theme.colors.cardBackground,
    borderColor: theme.colors.cardBorder,
    ...theme.shadows.card,
  }),
  input: daylightStyle(theme, {
    backgroundColor: theme.colors.inputBackground,
    borderColor: theme.colors.borderStrong,
  }),
  chip: daylightStyle(theme, {
    backgroundColor: theme.colors.chipBackground,
    borderColor: theme.colors.borderStrong,
  }),
  chipActive: daylightStyle(theme, {
    backgroundColor: theme.colors.chipBackgroundActive,
    borderColor: theme.colors.accent,
  }),
  secondaryChipActive: daylightStyle(theme, {
    backgroundColor: theme.colors.accentSecondarySoft,
    borderColor: theme.colors.accentSecondaryBorder,
  }),
  weekday: daylightStyle(theme, {
    backgroundColor: theme.colors.controlBackground,
    borderColor: theme.colors.borderStrong,
  }),
  weekdayActive: daylightStyle(theme, {
    backgroundColor: theme.colors.accent,
    borderColor: theme.colors.accent,
  }),
  modalBackdrop: daylightStyle(theme, { backgroundColor: theme.colors.overlay }),
  cityModal: daylightStyle(theme, {
    backgroundColor: theme.colors.surfaceElevated,
    borderColor: theme.colors.borderStrong,
    borderWidth: 1,
    ...theme.shadows.elevated,
  }),
  cityOption: daylightStyle(theme, {
    backgroundColor: theme.colors.controlBackground,
    borderColor: theme.colors.border,
  }),
  cityOptionActive: daylightStyle(theme, {
    backgroundColor: theme.colors.chipBackgroundActive,
    borderColor: theme.colors.accent,
  }),
  textPrimary: daylightStyle(theme, { color: theme.colors.textPrimary }),
  textSecondary: daylightStyle(theme, { color: theme.colors.textSecondary }),
  textMuted: daylightStyle(theme, { color: theme.colors.textMuted }),
  chipText: daylightStyle(theme, { color: theme.colors.textSecondary }),
  accentText: daylightStyle(theme, { color: theme.colors.accent }),
  accentSecondaryText: daylightStyle(theme, { color: theme.colors.accentSecondary }),
  accentBackground: daylightStyle(theme, { backgroundColor: theme.colors.accent }),
});

export default UploadEventScreen;
